import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { normalizeServiceMonth, serviceMonthLabel } from './serviceMonths.js';

const monthIdFromPickerValue = (value) => {
  if (!value || !value.isValid?.()) return null;
  const year = value.year();
  const month = String(value.month() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const NewMonthDialog = ({
  open,
  initialMonth = null,
  existingMonths = [],
  onClose,
  onCreate,
}) => {
  const [pickerValue, setPickerValue] = useState(() => dayjs(initialMonth || undefined));

  useEffect(() => {
    if (!open) return;
    setPickerValue(dayjs(initialMonth || undefined));
  }, [initialMonth, open]);

  const monthId = monthIdFromPickerValue(pickerValue);
  const existingMonthIds = useMemo(
    () => new Set(existingMonths.map((month) => normalizeServiceMonth(month.id || month)).filter(Boolean)),
    [existingMonths],
  );
  const alreadyExists = Boolean(monthId && existingMonthIds.has(monthId));
  const canSubmit = Boolean(monthId);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreate?.(monthId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack spacing={0.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            New service month
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {monthId ? serviceMonthLabel(monthId) : 'Select a billing month'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month and year"
            value={pickerValue}
            onChange={setPickerValue}
            views={['year', 'month']}
            openTo="month"
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: alreadyExists ? 'This month is already in the selector.' : ' ',
                inputProps: { 'aria-label': 'Month and year' },
              },
            }}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {alreadyExists ? 'Select month' : 'Add month'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewMonthDialog;
